import cx from 'classnames';
import React from 'react';
import { createPortal } from 'react-dom';

import { CopyIcon, EditIcon, ResendIcon } from './Icons';
import { useMessageActionOverlay } from './useMessageActionOverlay';
import { ASSISTANT_TEXTS } from '../../texts';

import styles from './styles.module.css';

const PANEL_WIDTH = 192;
const PANEL_GAP = 6;
const EDGE_MARGIN = 8;

const PANEL_HEIGHT_PER_ITEM = 40;
const PANEL_CHROME_HEIGHT = 16;

const getActions = ({ onCopy, onEditAndAsk, onResend }) => [
	{ id: 'copy', label: ASSISTANT_TEXTS.copy, icon: <CopyIcon />, onClick: onCopy },
	{ id: 'edit', label: ASSISTANT_TEXTS.editAndAsk, icon: <EditIcon />, onClick: onEditAndAsk },
	{
		id: 'resend',
		label: ASSISTANT_TEXTS.resend,
		icon: <ResendIcon />,
		onClick: onResend,
		dividerBefore: true,
	},
];

const computePanelPosition = ({ messageRect, actionsCount }) => {
	const vvHeight = window.visualViewport?.height ?? window.innerHeight;
	const vvWidth = window.innerWidth;
	const panelH = actionsCount * PANEL_HEIGHT_PER_ITEM + PANEL_CHROME_HEIGHT;

	let left = messageRect.left;
	let top = messageRect.bottom + PANEL_GAP;

	if (left + PANEL_WIDTH > vvWidth - EDGE_MARGIN) left = messageRect.right - PANEL_WIDTH;
	if (left < EDGE_MARGIN) left = EDGE_MARGIN;

	if (top + panelH > vvHeight - EDGE_MARGIN) top = messageRect.top - panelH - PANEL_GAP;
	if (top < EDGE_MARGIN) top = EDGE_MARGIN;

	return { left, top };
};

const MessageActionOverlay = ({
	messageRect,
	onCopy,
	onEditAndAsk,
	onResend,
	originRef,
	onClose,
}) => {
	const { firstItemRef, handleAction } = useMessageActionOverlay({ onClose, originRef });

	const actions = getActions({ onCopy, onEditAndAsk, onResend });
	const { left: panelLeft, top: panelTop } = computePanelPosition({
		messageRect,
		actionsCount: actions.length,
	});

	return createPortal(
		<div
			className={styles.overlay}
			data-testid="messageActionOverlay"
			role="dialog"
			aria-modal="true"
			aria-label="Message actions"
		>
			<div className={styles.backdrop} aria-hidden="true" onClick={() => handleAction()} />
			<div
				className={styles.panel}
				role="menu"
				style={{ '--panel-left': `${panelLeft}px`, '--panel-top': `${panelTop}px` }}
			>
				{actions.map((action, i) => (
					<React.Fragment key={action.id}>
						{action.dividerBefore && <div className={styles.divider} role="separator" />}
						<button
							ref={i === 0 ? firstItemRef : undefined}
							type="button"
							role="menuitem"
							className={cx(styles.actionItem)}
							data-testid={`overlayAction-${action.id}`}
							onClick={() => handleAction(action.onClick)}
						>
							<span className={styles.actionIcon} aria-hidden="true">
								{action.icon}
							</span>
							{action.label}
						</button>
					</React.Fragment>
				))}
			</div>
		</div>,
		document.body
	);
};

export default MessageActionOverlay;
